import React, { useState } from 'react';
import { X, ScrollText, Shield, Cookie, RotateCcw } from 'lucide-react';

const iconMap = {
  terms: ScrollText,
  privacy: Shield,
  cookies: Cookie,
  refund: RotateCcw
};

const LegalModal = ({ isOpen, onClose, data, type }) => {
  if (!isOpen || !data) return null;

  const Icon = iconMap[type] || ScrollText;

  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return <div key={i} className="h-3" />;

      // Bold text processing
      const parts = line.split(/\*\*(.*?)\*\*/g);
      const rendered = parts.map((part, j) =>
        j % 2 === 1
          ? <strong key={j} className="text-white font-semibold">{part}</strong>
          : <span key={j}>{part}</span>
      );

      // Bullet points
      if (line.trim().startsWith('•')) {
        return (
          <div key={i} className="flex gap-3 pl-4 py-0.5">
            <span className="text-[#2EC866] mt-0.5 flex-shrink-0">•</span>
            <span className="text-gray-400 text-sm leading-relaxed">{rendered}</span>
          </div>
        );
      }

      return <p key={i} className="text-gray-400 text-sm leading-relaxed">{rendered}</p>;
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        style={{ animation: 'fadeIn 0.2s ease-out' }}
      />

      {/* Modal */}
      <div
        className="relative bg-[#111] border border-gray-800/60 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl shadow-black/50"
        style={{ animation: 'modalSlideUp 0.3s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-800/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2EC866]/10 rounded-xl flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#2EC866]" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                {data.title}
              </h2>
              {data.lastUpdated && (
                <p className="text-gray-600 text-xs mt-0.5">Last updated: {data.lastUpdated}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6 custom-scrollbar">
          <div className="space-y-8">
            {data.sections.map((section, index) => (
              <div key={index}>
                {section.heading && (
                  <h3 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
                    <span className="w-1 h-5 bg-[#2EC866] rounded-full flex-shrink-0" />
                    {section.heading}
                  </h3>
                )}
                <div className="space-y-2">
                  {renderContent(section.content)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-800/50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-gray-600 text-xs">Luxomarket Ltd — www.luxo-market.com</p>
            <button
              onClick={onClose}
              className="bg-[#2EC866] hover:bg-[#25a855] text-white text-sm font-medium px-5 py-2 rounded-lg transition-all duration-300"
            >
              I Understand
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #555; }
      `}</style>
    </div>
  );
};

export default LegalModal;
